import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import ThemeToggle from '../../components/UI/ThemeToggle';
import PasswordInput from '../../components/SuperAdmin/PasswordInput';
import { getDefaultLandingPath } from '../../utils/superAdminAccess';
import './SuperAdminLogin.scss';

// Builds the {isSuperAdmin, hasPermission} shape getDefaultLandingPath expects,
// straight from an admin object (used right after login, before context re-renders).
const landingPathFor = (admin) => getDefaultLandingPath({
  isSuperAdmin: admin?.role === 'super_admin',
  hasPermission: (permission) => admin?.permissions?.includes(permission) || false,
});

// "Remember me" keeps the email pre-filled on this device (never the password)
const REMEMBER_EMAIL_KEY = 'superadmin_remember_email';

const readRememberedEmail = () => {
  try {
    return localStorage.getItem(REMEMBER_EMAIL_KEY) || '';
  } catch {
    return '';
  }
};

// Forgot-password flow steps (mirrors the org admin login OTP flow)
const MODE = {
  LOGIN: 'login',
  FP_EMAIL: 'fp_email', // enter email → OTP emailed
  FP_OTP: 'fp_otp', // enter the 6-digit OTP
  FP_RESET: 'fp_reset', // set a new password
};

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SuperAdminLogin = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, loading: authLoading, superAdmin } = useSuperAdmin();
  const [email, setEmail] = useState(readRememberedEmail);
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => !!readRememberedEmail());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Forgot-password state
  const [mode, setMode] = useState(MODE.LOGIN);
  const [fpEmail, setFpEmail] = useState('');
  const [fpOtp, setFpOtp] = useState('');
  const [fpNewPassword, setFpNewPassword] = useState('');
  const [fpConfirmPassword, setFpConfirmPassword] = useState('');
  const [fpResetToken, setFpResetToken] = useState('');
  const [fpError, setFpError] = useState('');
  const [fpLoading, setFpLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [info, setInfo] = useState('');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate(landingPathFor(superAdmin), { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, superAdmin]);

  // Resend OTP countdown
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="superadmin-login">
        <div className="login-container">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render login form if already authenticated
  if (isAuthenticated) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    try {
      const result = await login(email, password, rememberMe);

      if (result.success) {
        try {
          if (rememberMe) localStorage.setItem(REMEMBER_EMAIL_KEY, email.trim());
          else localStorage.removeItem(REMEMBER_EMAIL_KEY);
        } catch {
          /* storage unavailable — not critical */
        }
        navigate(landingPathFor(result.admin));
      } else {
        setError(result.message || 'Login failed');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Forgot password ──────────────────────────────────────────────
  const openForgotPassword = () => {
    setMode(MODE.FP_EMAIL);
    setFpEmail(email.trim());
    setFpOtp('');
    setFpNewPassword('');
    setFpConfirmPassword('');
    setFpResetToken('');
    setFpError('');
    setError('');
    setInfo('');
  };

  const backToLogin = () => {
    setMode(MODE.LOGIN);
    setFpError('');
  };

  const requestOtp = async () => {
    const response = await api.post('/superadmin/auth/forgot-password', { email: fpEmail.trim() });
    return response.data;
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setFpError('');
    if (!EMAIL_RX.test(fpEmail.trim())) {
      setFpError('Please enter a valid email address');
      return;
    }
    setFpLoading(true);
    try {
      const data = await requestOtp();
      if (data?.success) {
        toast.success('If this email belongs to a superadmin, an OTP has been sent to it');
        setMode(MODE.FP_OTP);
        setResendIn(60);
      } else {
        setFpError(data?.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      setFpError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setFpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendIn > 0 || fpLoading) return;
    setFpError('');
    setFpLoading(true);
    try {
      const data = await requestOtp();
      if (data?.success) {
        toast.success('A new OTP has been sent');
        setFpOtp('');
        setResendIn(60);
      } else {
        setFpError(data?.message || 'Failed to resend OTP.');
      }
    } catch (err) {
      setFpError(err.response?.data?.message || 'Failed to resend OTP.');
    } finally {
      setFpLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setFpError('');
    if (!/^\d{6}$/.test(fpOtp.trim())) {
      setFpError('Please enter the 6-digit OTP from your email');
      return;
    }
    setFpLoading(true);
    try {
      const response = await api.post('/superadmin/auth/verify-otp', {
        email: fpEmail.trim(),
        otp: fpOtp.trim(),
      });
      const token = response.data?.data?.resetToken;
      if (response.data?.success && token) {
        setFpResetToken(token);
        setMode(MODE.FP_RESET);
        toast.success('OTP verified — set your new password');
      } else {
        setFpError(response.data?.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setFpError(err.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setFpLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setFpError('');
    if (fpNewPassword.length < 8) {
      setFpError('Password must be at least 8 characters');
      return;
    }
    if (fpNewPassword !== fpConfirmPassword) {
      setFpError('Passwords do not match');
      return;
    }
    setFpLoading(true);
    try {
      const response = await api.post('/superadmin/auth/reset-password', {
        resetToken: fpResetToken,
        newPassword: fpNewPassword,
        confirmPassword: fpConfirmPassword,
      });
      if (response.data?.success) {
        toast.success('Password reset successfully');
        setEmail(fpEmail.trim());
        setPassword('');
        setInfo('Password updated. Sign in with your new password.');
        setMode(MODE.LOGIN);
      } else {
        setFpError(response.data?.message || 'Failed to reset password.');
      }
    } catch (err) {
      setFpError(err.response?.data?.message || 'Failed to reset password.');
    } finally {
      setFpLoading(false);
    }
  };

  const headerText = {
    [MODE.LOGIN]: ['Platform Administration', 'Sign in to access the superadmin dashboard'],
    [MODE.FP_EMAIL]: ['Forgot Password', "Enter your superadmin email — we'll send you a verification code (OTP)"],
    [MODE.FP_OTP]: ['Verify OTP', `Enter the 6-digit code sent to ${fpEmail.trim() || 'your email'}`],
    [MODE.FP_RESET]: ['Set New Password', 'Choose a new password for your superadmin account'],
  }[mode];

  return (
    <div className="superadmin-login">
      {/* Same theme toggle as the admin login — positioned top-right */}
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>
      <div className="login-container">
        <div className="login-header">
          <div className="logo">
            <span className="logo-icon">🔐</span>
            <span className="logo-text">TeleValley Admin</span>
          </div>
          <h1>{headerText[0]}</h1>
          <p>{headerText[1]}</p>
        </div>

        {mode === MODE.LOGIN && (
          <form onSubmit={handleSubmit} className="login-form">
            {error && <div className="error-message">{error}</div>}
            {info && <div className="info-message">{info}</div>}

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="superadmin@televalley.in"
                autoComplete="username"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
                disabled={loading}
              />
            </div>

            <div className="login-options">
              <label className="remember-me" htmlFor="remember-me">
                <input
                  type="checkbox"
                  id="remember-me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                />
                <span>Remember me</span>
              </label>
              <button type="button" className="link-button" onClick={openForgotPassword} disabled={loading}>
                Forgot Password?
              </button>
            </div>

            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}

        {mode === MODE.FP_EMAIL && (
          <form onSubmit={handleSendOtp} className="login-form" noValidate>
            {fpError && <div className="error-message">{fpError}</div>}
            <div className="form-group">
              <label htmlFor="fp-email">Email</label>
              <input
                type="email"
                id="fp-email"
                value={fpEmail}
                onChange={(e) => setFpEmail(e.target.value)}
                placeholder="superadmin@televalley.in"
                autoComplete="username"
                required
                disabled={fpLoading}
              />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={fpLoading}>
              {fpLoading ? 'Sending OTP...' : 'Send OTP'}
            </button>
            <div className="form-links">
              <button type="button" className="link-button" onClick={backToLogin} disabled={fpLoading}>
                ← Back to Sign In
              </button>
            </div>
          </form>
        )}

        {mode === MODE.FP_OTP && (
          <form onSubmit={handleVerifyOtp} className="login-form" noValidate>
            {fpError && <div className="error-message">{fpError}</div>}
            <div className="form-group">
              <label htmlFor="fp-otp">Verification Code (OTP)</label>
              <input
                type="text"
                id="fp-otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={fpOtp}
                onChange={(e) => setFpOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit OTP"
                required
                disabled={fpLoading}
              />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={fpLoading}>
              {fpLoading ? 'Verifying...' : 'Verify OTP'}
            </button>
            <div className="form-links">
              <button type="button" className="link-button" onClick={() => setMode(MODE.FP_EMAIL)} disabled={fpLoading}>
                ← Change email
              </button>
              <button
                type="button"
                className="link-button"
                onClick={handleResendOtp}
                disabled={fpLoading || resendIn > 0}
              >
                {resendIn > 0 ? `Resend OTP in ${resendIn}s` : 'Resend OTP'}
              </button>
            </div>
          </form>
        )}

        {mode === MODE.FP_RESET && (
          <form onSubmit={handleResetPassword} className="login-form" noValidate>
            {fpError && <div className="error-message">{fpError}</div>}
            <div className="form-group">
              <label htmlFor="fp-new-password">New Password</label>
              <PasswordInput
                id="fp-new-password"
                value={fpNewPassword}
                onChange={(e) => setFpNewPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                autoComplete="new-password"
                required
                disabled={fpLoading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="fp-confirm-password">Confirm New Password</label>
              <PasswordInput
                id="fp-confirm-password"
                value={fpConfirmPassword}
                onChange={(e) => setFpConfirmPassword(e.target.value)}
                placeholder="Re-enter the new password"
                autoComplete="new-password"
                required
                disabled={fpLoading}
              />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={fpLoading}>
              {fpLoading ? 'Saving...' : 'Reset Password'}
            </button>
            <div className="form-links">
              <button type="button" className="link-button" onClick={backToLogin} disabled={fpLoading}>
                ← Back to Sign In
              </button>
            </div>
          </form>
        )}

        <div className="login-footer">
          <p>This is a secure area for platform administrators only.</p>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminLogin;
